import {withHistoryUpdate} from "@buildwithflux/core";
import {isEqual} from "lodash";

import {buildDocumentStateUpdate, performDocumentMutation} from "../documentMutations";
import {notifyServicesOfAction} from "../serviceNotifier";
import type {DocumentStoreState} from "../types";
import {applyLockObjects, handleSelectObjectsNatively as applySelectObjects} from "../actions/selectionActions";
import {processUndoRedoInStore} from "../undoRedoProcessing";

import type {SliceDependencies} from "./types";

type SelectionSliceState = Pick<DocumentStoreState, "lockObjects" | "selectObjects">;

export function createSelectionSlice(deps: SliceDependencies): SelectionSliceState {
    const {primitives, set, get, services, actionEventBus} = deps;

    return {
        lockObjects: (objectUids, mode, locked) => {
            performDocumentMutation(primitives, (draft) => applyLockObjects(draft, objectUids, locked, mode), {
                actionType: "lockObjects",
                canUndo: true,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: true,
                additionalPayload: {
                    hasInheritedRules: false,
                    hasInheritedCalculations: false,
                    requiresDescendantRebake: false,
                    requiresNetDescendantRebake: false,
                },
            });
        },

        selectObjects: (selectedObjectUids, userHasPermission) => {
            const canUndo = !!userHasPermission;

            if (!canUndo) {
                // Fast path: no undo/redo needed, just update selection directly
                const prev = get().document;
                const next = applySelectObjects(prev, selectedObjectUids);
                if (next !== prev) {
                    set(buildDocumentStateUpdate(next), false, "document/selectObjects");
                }
                notifyServicesOfAction(
                    {
                        type: "selectObjects",
                        payload: {
                            selectedObjectUids,
                            canUndo: false,
                            updatesDocumentTimestamp: false,
                            shouldGenerateActionRecord: false,
                        },
                    },
                    services,
                    actionEventBus,
                );
                return;
            }

            // Slow path: need to generate patches for undo/redo
            const document = get().document;
            if (!document) {
                return;
            }

            // Check if selection actually changed before generating patches
            const currentSelection = document.selectedObjectUids;
            const selectionChanged =
                currentSelection?.length !== selectedObjectUids.length ||
                !isEqual(currentSelection, selectedObjectUids);

            const action = {
                type: "selectObjects",
                payload: {
                    selectedObjectUids,
                    canUndo: true,
                    updatesDocumentTimestamp: false,
                    shouldGenerateActionRecord: false,
                },
            };

            if (!selectionChanged) {
                notifyServicesOfAction(action, services, actionEventBus);
                return;
            }

            const newDocument = withHistoryUpdate(document, (draft) => {
                draft.selectedObjectUids = selectedObjectUids;
            });

            if (newDocument !== document) {
                set(buildDocumentStateUpdate(newDocument), false, "document/selectObjects");
                const undoRedoState = deps.undoRedoStore.getState();
                const stackOps = {
                    pushToUndoStack: undoRedoState.pushToUndoStack,
                    pushToRedoStack: undoRedoState.pushToRedoStack,
                    clearRedoStack: undoRedoState.clearRedoStack,
                };
                processUndoRedoInStore(action, newDocument, stackOps);
            }

            notifyServicesOfAction(action, services, actionEventBus);
        },
    };
}
